/**
 * ХЭЛТЭС ↔ ХУУЛИЙН ЭТГЭЭДИЙН ТОХИРОЛ — API (`HrEmployeesService.create`, 400) ба админ («Ажилтан шинээр бүртгэх»
 * цонхны хэлтсийн сонгогч) НЭГ дүрэм (docs/39 §6.6, docs/30 §3).
 *
 * ! `hr_departments.organization_id` ГУРВАН утгатай: тодорхой байгууллага · БҮЛЭГ (`isGroup`, «хоёулангийн дундын») ·
 * ! `null` («хараахан шийдэгдээгүй»). Хоосон ДЭД хэлтэс нь ЭЦГЭЭСЭЭ ӨВЛӨНӨ (`hr-lookup-crud.service`
 * ! `assertOrganizationConsistent` — каскадаар бичдэггүй, хориглолоор баримталдаг). Тиймээс хэлтсийн ҮЙЛЧЛЭХ харьяалал нь
 * ! өөрөөсөө эхлэн эцэг рүү өгсөхөд таарсан ЭХНИЙ хоосон биш утга.
 *
 * ! Id-г МӨР болгож өгнө (API нь `BigInt`-ийг `String()`-ээр хөрвүүлнэ) — админы лавлахын хэлбэр мөр.
 */

export type HrDepartmentOrganizationNode = {
  id: string
  parentId: string | null
  organizationId: string | null
}

export type HrOrganizationKindNode = {
  id: string
  isGroup: boolean
}

/**
 * Хэлтсийн ҮЙЛЧЛЭХ байгууллага — өөрөөсөө эхлэн эцэг рүү өгсөж олсон эхний `organizationId`, эсвэл `null`
 * (гинж бүхэлдээ шийдэгдээгүй / хэлтэс олдсонгүй).
 *
 * ! МӨЧЛӨГӨӨС ХАМГААЛНА: `parentId` нь гогцоо үүсгэвэл `seen` нь давталтыг зогсооно.
 */
export const effectiveDepartmentOrganization = (
  departments: readonly HrDepartmentOrganizationNode[] | ReadonlyMap<string, HrDepartmentOrganizationNode>,
  departmentId: string
): string | null => {
  /* Мод (`Map`) дамжуулбал дахин бүтээхгүй — олон ажилтан-өдрийг нэг контекстоор бодох үед (резолвер) өртөг багасна. */
  const byId =
    departments instanceof Map
      ? (departments as ReadonlyMap<string, HrDepartmentOrganizationNode>)
      : new Map((departments as readonly HrDepartmentOrganizationNode[]).map(row => [String(row.id), row]))
  const seen = new Set<string>()

  let cursor = byId.get(String(departmentId))

  while (cursor && !seen.has(String(cursor.id))) {
    seen.add(String(cursor.id))

    if (cursor.organizationId !== null && cursor.organizationId !== undefined) return String(cursor.organizationId)

    cursor = cursor.parentId ? byId.get(String(cursor.parentId)) : undefined
  }

  return null
}

/**
 * Энэ хэлтэст `organizationId`-гийн ажилтныг суулгаж болох уу.
 *
 *   үйлчлэх харьяалал `null`       → тийм (шийдэгдээгүй — өнөөдөр ихэнх хэлтэс ийм; хатуу шаардвал бүртгэл бүр гацна)
 *   үйлчлэх харьяалал нь БҮЛЭГ     → тийм (дундын)
 *   үйлчлэх харьяалал = сонгосон   → тийм
 *   бусад                          → ҮГҮЙ (нөгөө хуулийн этгээдийн хэлтэс)
 *
 * ! Жагсаалтад байхгүй байгууллагыг (устгагдсан г.м.) «бүлэг биш» гэж үзнэ — чимээгүй зөвшөөрөхгүй.
 */
export const departmentFitsOrganization = (input: {
  departments: readonly HrDepartmentOrganizationNode[]
  organizations: readonly HrOrganizationKindNode[]
  departmentId: string
  organizationId: string
}): boolean => {
  const owner = effectiveDepartmentOrganization(input.departments, input.departmentId)

  if (owner === null) return true
  if (owner === String(input.organizationId)) return true

  return input.organizations.some(row => String(row.id) === owner && row.isGroup)
}
